import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { investigate } from '../api/scout';
import ConversationTimeline from '../components/ConversationTimeline';
import { useTheme, ACCENT } from '../contexts/ThemeContext';

const VERDICT_STYLES = {
  'Fair':           { bg: 'rgba(239, 68, 68, 0.06)', border: '#F07070', color: '#F07070' },
  'Partially Fair': { bg: 'rgba(234, 179, 8, 0.06)', border: '#E5C040', color: '#E5C040' },
  'Unfair':         { bg: 'rgba(34, 197, 94, 0.06)', border: '#50D080', color: '#50D080' },
  'Inconclusive':   { bg: 'rgba(148, 163, 184, 0.06)', border: '#8890A0', color: '#8890A0' },
};

const SEVERITY_STYLES = {
  high:   { color: '#F07070' },
  medium: { color: '#E5C040' },
  low:    { color: '#50D080' },
};

const CATEGORY_STYLES = {
  Service:       { color: '#5B9BF7' },
  Product:       { color: '#B78AF7' },
  Operations:    { color: '#E5C040' },
  AI:            { color: '#3DD4E8' },
  Noncompliance: { color: '#8890A0' },
};

const SOURCE_TAGS = ['Amazon', 'Reddit', 'Email', 'Social', 'Other'];

const CONFIDENCE_STYLES = {
  high:   { color: '#50D080' },
  medium: { color: '#E5C040' },
  low:    { color: '#F07070' },
  none:   { color: '#8890A0' },
};

function Badge({ color, children }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: '9px', fontWeight: 700,
      padding: '2px 8px', borderRadius: '6px',
      background: `${color}15`,
      color,
      borderLeft: `2px solid ${color}`,
      letterSpacing: '0.06em', textTransform: 'uppercase',
    }}>
      {children}
    </span>
  );
}

function ProgressSteps({ step }) {
  const { t } = useTheme();
  const steps = [
    { key: 'strategy', label: 'Building search strategy' },
    { key: 'search', label: 'Searching Zendesk' },
    { key: 'evaluate', label: 'Evaluating candidates' },
    { key: 'analyze', label: 'Analyzing match' },
  ];
  const currentIdx = steps.findIndex(s => s.key === step);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: '6px',
      padding: '24px', borderRadius: '10px',
      background: t.glass,
      backdropFilter: 'blur(40px)',
      WebkitBackdropFilter: 'blur(40px)',
      border: `1px solid ${t.border}`,
    }}>
      {steps.map((s, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '20px', height: '20px', borderRadius: '6px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '10px', fontWeight: 900,
              background: done ? 'rgba(80,208,128,0.10)' : active ? 'rgba(229, 46, 58,0.10)' : 'transparent',
              color: done ? '#50D080' : active ? ACCENT : t.textFaint,
              border: `1px solid ${done ? '#50D08030' : active ? '#E52E3A30' : t.border}`,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {done ? '✓' : i + 1}
            </div>
            <span style={{
              fontSize: '11px', fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              color: done ? '#50D080' : active ? t.text : t.textFaint,
            }}>
              {s.label}
              {active && <span style={{ marginLeft: '4px', opacity: 0.4 }}>...</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function Investigate() {
  const location = useLocation();
  const { t } = useTheme();
  const [text, setText] = useState('');
  const [source, setSource] = useState('Other');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showConversation, setShowConversation] = useState(false);
  const [showStrategy, setShowStrategy] = useState(false);
  const hasAutoRun = useRef(false);

  useEffect(() => {
    if (location.state?.text && !hasAutoRun.current) {
      hasAutoRun.current = true;
      setText(location.state.text);
      runInvestigation(location.state.text);
    }
    window.history.replaceState({}, document.title);
  }, []); // eslint-disable-line

  async function runInvestigation(inputText) {
    const t = inputText || text;
    if (!t.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    setShowConversation(false);
    setShowStrategy(false);
    setStep('strategy');
    await new Promise(r => setTimeout(r, 600));
    setStep('search');
    await new Promise(r => setTimeout(r, 400));
    setStep('evaluate');
    try {
      const data = await investigate(t.trim());
      setStep('analyze');
      await new Promise(r => setTimeout(r, 300));
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setStep(null);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    runInvestigation();
  }

  const inv = result?.investigation;
  const verdict = inv ? VERDICT_STYLES[inv.verdict] || VERDICT_STYLES['Inconclusive'] : null;
  const severity = inv ? SEVERITY_STYLES[inv.severity] || SEVERITY_STYLES.low : null;
  const category = inv ? CATEGORY_STYLES[inv.category] || CATEGORY_STYLES.Service : null;
  const conf = CONFIDENCE_STYLES[result?.matchConfidence] || CONFIDENCE_STYLES.none;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 style={{
        fontSize: '20px', fontWeight: 900, color: t.text,
        letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '24px',
      }}>
        Investigate External Complaint
      </h1>

      {/* Input form */}
      <form onSubmit={handleSubmit} className="card" style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block', fontSize: '10px', fontWeight: 700,
          color: t.textDim, marginBottom: '10px',
          letterSpacing: '0.1em', textTransform: 'uppercase',
        }}>
          Paste the review, complaint, or customer text
        </label>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Paste Amazon review, Reddit post, email, social media complaint..."
          rows={5}
          className="textarea"
          style={{ width: '100%', marginBottom: '14px' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{
              fontSize: '9px', color: t.textDim, fontWeight: 700,
              marginRight: '6px', letterSpacing: '0.1em', textTransform: 'uppercase',
            }}>Source:</span>
            {SOURCE_TAGS.map(tag => (
              <button
                key={tag}
                type="button"
                onClick={() => setSource(tag)}
                style={{
                  fontSize: '10px', fontWeight: 700, padding: '4px 10px', borderRadius: '6px',
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  border: source === tag ? '1px solid rgba(229, 46, 58, 0.3)' : '1px solid transparent',
                  cursor: 'pointer', transition: 'all 0.1s',
                  background: source === tag ? 'rgba(229, 46, 58, 0.08)' : 'transparent',
                  color: source === tag ? ACCENT : t.textDim,
                }}
              >
                {tag}
              </button>
            ))}
          </div>
          <button type="submit" disabled={!text.trim() || loading} className="btn-primary">
            INVESTIGATE
          </button>
        </div>
      </form>

      {error && (
        <div style={{
          marginBottom: '20px', padding: '12px 16px', borderRadius: '10px',
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)',
          borderLeft: '2px solid #F07070',
          color: '#F07070', fontSize: '12px', fontWeight: 600,
        }}>
          {error}
        </div>
      )}

      {loading && step && <ProgressSteps step={step} />}

      {/* RESULTS */}
      {result && !loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>

          {/* AI Strategy */}
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{
                fontSize: '9px', fontWeight: 900, color: t.textDim,
                letterSpacing: '0.12em', textTransform: 'uppercase',
              }}>
                AI STRATEGY
              </span>
              {result.strategy?.product && <Badge color={ACCENT}>{result.strategy.product}</Badge>}
              {result.strategy?.definitiveId?.value && result.strategy.definitiveId.type !== 'null' && (
                <Badge color="#5B9BF7">{result.strategy.definitiveId.value}</Badge>
              )}
              {result.strategy?.keyDetails?.issueDescription && (
                <Badge color="#8890A0">{result.strategy.keyDetails.issueDescription}</Badge>
              )}
              <Badge color={conf.color}>{result.matchConfidence} match</Badge>
              <span className="font-mono" style={{ fontSize: '10px', color: t.textDim, marginLeft: '4px' }}>
                {result.resultCount} result{result.resultCount !== 1 ? 's' : ''} / {result.queries?.length || 0} quer{(result.queries?.length || 0) !== 1 ? 'ies' : 'y'}
              </span>
            </div>

            {result.strategy?.reasoning && (
              <p style={{ fontSize: '12px', color: t.textMuted, margin: '10px 0 0', lineHeight: 1.55 }}>
                {result.strategy.reasoning}
              </p>
            )}

            <button
              onClick={() => setShowStrategy(!showStrategy)}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: '10px', color: ACCENT, padding: '8px 0 0', fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase',
              }}
            >
              {showStrategy ? '▾ HIDE' : '▸ SHOW'} SEARCH DETAILS
            </button>

            {showStrategy && (
              <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {result.queries?.map((q, i) => (
                  <div key={i} style={{
                    padding: '8px 12px', borderRadius: '10px',
                    background: 'rgba(255,255,255,0.02)', fontSize: '12px',
                    display: 'flex', alignItems: 'center', gap: '8px',
                    border: `1px solid ${t.borderLight}`,
                  }}>
                    <Badge color={q.tier === 1 ? '#50D080' : q.tier === 2 ? '#E5C040' : '#8890A0'}>
                      {q.tier ? `T${q.tier}` : q.type || '?'}
                    </Badge>
                    <code className="font-mono" style={{ color: t.text, flex: 1, fontSize: '11px' }}>{q.query}</code>
                    <span className="font-mono" style={{ color: t.textDim, fontSize: '10px' }}>
                      {q.error ? 'FAIL' : `${q.results}`}
                    </span>
                    {q.fallback && <Badge color="#B78AF7">FALLBACK</Badge>}
                  </div>
                ))}
                {result.evaluation?.bestMatch && (
                  <div style={{
                    padding: '10px 14px', borderRadius: '10px',
                    background: 'rgba(91,155,247,0.04)', border: '1px solid rgba(91,155,247,0.10)',
                    borderLeft: '2px solid #5B9BF7',
                    fontSize: '12px', color: t.textSecondary, lineHeight: 1.55,
                  }}>
                    <span style={{ fontWeight: 900, color: '#5B9BF7', letterSpacing: '0.04em' }}>AI EVAL: </span>
                    {result.evaluation.bestMatch.reasoning}
                  </div>
                )}
                {result.evaluation?.evaluations?.filter(e => !e.matches).map((e, i) => (
                  <div key={i} style={{
                    padding: '6px 12px', borderRadius: '10px',
                    background: 'rgba(240,112,112,0.04)', fontSize: '11px', color: t.textMuted,
                    borderLeft: '2px solid rgba(240,112,112,0.3)',
                  }}>
                    <span style={{ color: '#F07070', fontWeight: 700 }}>ELIM #{e.ticketId}:</span> {e.reasoning}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* MATCHED TICKET + VERDICT */}
          {result.matchedTicket && inv ? (
            <>
              {/* Verdict */}
              <div style={{
                padding: '20px', borderRadius: '10px',
                background: verdict.bg,
                border: `1px solid ${verdict.border}30`,
                borderLeft: `3px solid ${verdict.border}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                  <div>
                    <div style={{
                      fontSize: '9px', fontWeight: 900, color: t.textDim,
                      letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '4px',
                    }}>VERDICT</div>
                    <div style={{ fontSize: '22px', fontWeight: 900, color: verdict.color, letterSpacing: '0.04em' }}>
                      {inv.verdict}
                    </div>
                    <div style={{ fontSize: '12px', color: t.textMuted, marginTop: '4px' }}>{inv.verdictReason}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <Badge color={category.color}>{inv.category}</Badge>
                    <Badge color={severity.color}>{inv.severity?.toUpperCase()}</Badge>
                  </div>
                </div>

                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 14px', borderRadius: '10px',
                  background: 'rgba(0,0,0,0.2)', border: `1px solid ${t.border}`,
                }}>
                  <span style={{
                    fontSize: '9px', fontWeight: 900, color: t.textDim,
                    letterSpacing: '0.12em',
                  }}>MATCHED</span>
                  <Link to={`/ticket/${result.matchedTicket.id}`} className="font-mono" style={{
                    fontSize: '13px', fontWeight: 700, color: ACCENT, textDecoration: 'none',
                  }}>
                    #{result.matchedTicket.id}
                  </Link>
                  <span style={{ fontSize: '13px', color: t.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {result.matchedTicket.subject}
                  </span>
                  <Badge color={t.textDim}>{result.matchedTicket.status}</Badge>
                  <Badge color={conf.color}>{result.matchConfidence}</Badge>
                </div>
              </div>

              {/* Claim vs Reality */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div className="card hud-bar-left" style={{ borderLeftColor: '#F07070' }}>
                  <div style={{
                    fontSize: '9px', fontWeight: 900, color: '#F07070',
                    letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '8px',
                  }}>CUSTOMER CLAIMS</div>
                  <p style={{ fontSize: '13px', color: t.text, lineHeight: 1.6, margin: 0 }}>{inv.whatCustomerClaimed}</p>
                </div>
                <div className="card hud-bar-left" style={{ borderLeftColor: '#5B9BF7' }}>
                  <div style={{
                    fontSize: '9px', fontWeight: 900, color: '#5B9BF7',
                    letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '8px',
                  }}>WHAT ACTUALLY HAPPENED</div>
                  <p style={{ fontSize: '13px', color: t.text, lineHeight: 1.6, margin: 0 }}>{inv.whatActuallyHappened}</p>
                </div>
              </div>

              {/* Root Cause + Recommendation */}
              <div className="card" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div className="hud-bar-left">
                  <div style={{
                    fontSize: '9px', fontWeight: 900, color: t.textDim,
                    letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '6px',
                  }}>ROOT CAUSE</div>
                  <p style={{ fontSize: '13px', color: t.text, margin: 0, lineHeight: 1.6 }}>{inv.rootCause}</p>
                </div>
                <div className="hud-bar-left" style={{ borderLeftColor: ACCENT }}>
                  <div style={{
                    fontSize: '9px', fontWeight: 900, color: ACCENT,
                    letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '6px',
                  }}>RECOMMENDATION</div>
                  <p style={{ fontSize: '13px', color: t.text, margin: 0, lineHeight: 1.6 }}>{inv.recommendation}</p>
                </div>
              </div>

              {/* Conversation */}
              {result.ticketData?.comments && (
                <div>
                  <button onClick={() => setShowConversation(!showConversation)} className="btn-secondary" style={{ height: '36px', fontSize: '10px' }}>
                    {showConversation ? 'HIDE' : 'SHOW'} CONVERSATION ({result.ticketData.comments.length})
                  </button>
                  {showConversation && (
                    <div style={{ marginTop: '8px' }}>
                      <ConversationTimeline comments={result.ticketData.comments} users={result.ticketData.users} />
                    </div>
                  )}
                </div>
              )}

              {/* Other results */}
              {result.otherResults?.length > 0 && (
                <div className="card">
                  <span style={{
                    fontSize: '9px', fontWeight: 900, color: t.textDim,
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    display: 'block', marginBottom: '10px',
                  }}>
                    OTHER MATCHES ({result.otherResults.length})
                  </span>
                  {result.otherResults.map((ticket, i) => (
                    <div key={ticket.id} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '8px 0',
                      borderBottom: i < result.otherResults.length - 1 ? `1px solid ${t.border}` : 'none',
                    }}>
                      <Link to={`/ticket/${ticket.id}`} className="font-mono" style={{ color: ACCENT, fontSize: '12px', fontWeight: 700, textDecoration: 'none' }}>
                        #{ticket.id}
                      </Link>
                      <span style={{ fontSize: '13px', color: t.text, flex: 1 }}>{ticket.subject}</span>
                      <Badge color={t.textDim}>{ticket.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : result && !result.matchedTicket ? (
            <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
              <p style={{
                fontSize: '13px', color: t.textMuted, margin: '0 0 8px', fontWeight: 700,
                letterSpacing: '0.04em', textTransform: 'uppercase',
              }}>
                No matching ticket found
              </p>
              <p style={{ fontSize: '12px', color: t.textDim, margin: '0 0 16px' }}>
                {result.strategy?.reasoning || 'Try pasting text with more specific details.'}
              </p>
              {result.evaluation?.fallbackReason && (
                <p style={{ fontSize: '11px', color: '#E5C040', margin: '0 0 16px' }}>{result.evaluation.fallbackReason}</p>
              )}
              <Link to="/" style={{
                fontSize: '11px', color: ACCENT, textDecoration: 'none',
                fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>
                ← BACK TO DASHBOARD
              </Link>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
